import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import {
  Typography, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, DialogActions,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { useAptorApi } from '../../../../../Api';
import { UserContext } from '../../../../../Context/UserContext/UserContext';
import { useIntl } from 'react-intl';
import { IProps, StepData } from './types';
import DeleteStep1CompanyUnit from './DeleteStep1CompanyUnit';
import DeleteStep2LawsTable from './DeleteStep2LawsTable';
import DeleteStep3Confirm from './DeleteStep3Confirm';

const PRIMARY = '#c4122f';

export const DeleteLawWizard = ({
  open,
  onClose,
  onDeleteComplete,
}: IProps) => {
  const { companyUnits, id: userId } = useContext(UserContext);
  const { api } = useAptorApi();
  const { formatMessage } = useIntl();

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCompanyUnit, setSelectedCompanyUnit] = useState<any>(null);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectedRowsData, setSelectedRowsData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stepData, setStepData] = useState<StepData>({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const sessionHydrated = useRef(false);

  useEffect(() => {
    if (open && !sessionHydrated.current) {
      if (companyUnits && companyUnits.length === 1) {
        const unit = companyUnits[0];
        setSelectedCompanyUnit(unit);
        setStepData({ CustomerCompanyUnitId: unit.id, _companyUnitName: unit.name });
        setCurrentStep(2);
      }
      sessionHydrated.current = true;
    }
  }, [open, companyUnits]);

  const handleStepNext = useCallback((data: Partial<StepData>) => {
    setStepData(prev => ({ ...prev, ...data }));
    if (data.CustomerCompanyUnitId && data.CustomerCompanyUnitId !== stepData.CustomerCompanyUnitId) {
      setSelectedRows([]);
      setSelectedRowsData([]);
    }
    setCurrentStep(prev => prev + 1);
  }, [stepData]);

  const handleStepBack = useCallback(() => {
    setCurrentStep(prev => Math.max(1, prev - 1));
  }, []);



  const handleClose = () => {
    setCurrentStep(1);
    setSelectedCompanyUnit(null);
    setSelectedRows([]);
    setSelectedRowsData([]);
    setStepData({});
    sessionHydrated.current = false;
    onClose();
  };

  const handleConfirmDelete = async () => {
    try {
      setIsLoading(true);
      if (selectedRows?.length > 0) {
        await api.removeMultipleCustomerLaw(selectedRows);
      }
      handleClose();
      onDeleteComplete();
    } catch (error) {
      setIsLoading(false);
    }
  };

  const totalSteps = 3;
  const activeSteps = Array(totalSteps).fill(null);
  
  const isStep1Skipped = companyUnits && companyUnits.length === 1;
  const effectiveTotalSteps = isStep1Skipped ? totalSteps - 1 : totalSteps;
  const effectiveCurrentStep = isStep1Skipped ? currentStep - 1 : currentStep;
  const effectiveActiveSteps = Array(effectiveTotalSteps).fill(null);

  const renderStep = () => {
    const commonProps = {
      currentData: stepData,
      isFirstStep: currentStep === 1,
      onCancel: handleClose,
    };

    switch (currentStep) {
      case 1:
        return (
          <DeleteStep1CompanyUnit
            {...commonProps}
            onNext={handleStepNext}
            onBack={handleStepBack}
            companyUnits={companyUnits || []}
            onCompanyUnitSelect={setSelectedCompanyUnit}
          />
        );
      case 2:
        return (
          <DeleteStep2LawsTable
            isFirstStep={false}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            selectedRowsData={selectedRowsData}
            setSelectedRowsData={setSelectedRowsData}
            selectedCompanyUnit={selectedCompanyUnit}
            onNext={() => handleStepNext({})}
            onBack={handleStepBack}
            onCancel={handleClose}
          />
        );
      case 3:
        return (
          <DeleteStep3Confirm
            isFirstStep={false}
            selectedRowsData={selectedRowsData}
            selectedCompanyUnit={selectedCompanyUnit}
            onConfirmDelete={handleConfirmDelete}
            onBack={handleStepBack}
            onCancel={handleClose}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Dialog open={open} maxWidth="lg" fullWidth onClose={handleClose}>
        <DialogTitle style={{ borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
  <Typography variant="h6" style={{ fontWeight: 600 }}>
              {formatMessage({ id: 'lawportal.our-law.Delete-law' }, { defaultMessage: 'Delete Laws' })}
            </Typography>

        </DialogTitle>

        <DialogContent style={{ paddingTop: '24px', minHeight: '400px' }}>
          <Box style={{ minHeight: '420px' }}>
            <Box style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Button variant="outlined" onClick={handleClose} style={{ borderColor: PRIMARY, color: PRIMARY }}>
                  {formatMessage({ id: 'form.cancel' })}
                </Button>
              </Box>
              {effectiveCurrentStep > 1 && (
                <Button variant="outlined" onClick={handleStepBack} style={{ borderColor: PRIMARY, color: PRIMARY }}>
                  {formatMessage({ id: 'component.back-button.back' })}
                </Button>
              )}
            </Box>

            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 16px',
                marginBottom: '12px',
                backgroundColor: '#fdf2f3',
                borderRadius: '6px',
                borderLeft: `4px solid ${PRIMARY}`,
                flexWrap: 'wrap',
                gap: '8px',
              }}
            >
              <Typography variant="body2" style={{ fontWeight: 700, color: PRIMARY, fontSize: '13px' }}>
                {formatMessage({ id: "law-portal.evalution.plan.step" }, { currentStep: effectiveCurrentStep, totalSteps: effectiveTotalSteps })}
              </Typography>

              <Box style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                {effectiveActiveSteps.map((_, idx) => (
                  <Box
                    key={idx}
                    style={{
                      width: idx === effectiveCurrentStep - 1 ? '20px' : '8px',
                      height: '8px',
                      borderRadius: '4px',
                      backgroundColor: idx < effectiveCurrentStep ? PRIMARY : '#e0e0e0',
                      transition: 'all 0.3s ease',
                    }}
                  />
                ))}
              </Box>

              <Typography variant="body2" style={{ fontWeight: 600, color: '#555', fontSize: '13px', textTransform: 'capitalize' }} />
            </Box>

            {renderStep()}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeleteLawWizard;
